import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const InventoryFilters = ({ onFiltersChange, onClearFilters }) => {
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    stockStatus: '',
    location: '',
    responsibleEmployee: ''
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const categories = [
    { value: '', label: 'Todas las Categorías' },
    { value: 'electronics', label: 'Electrónicos' },
    { value: 'office-supplies', label: 'Material de Oficina' },
    { value: 'furniture', label: 'Mobiliario' },
    { value: 'tools', label: 'Herramientas' },
    { value: 'consumables', label: 'Consumibles' }
  ];

  const stockStatuses = [
    { value: '', label: 'Todos los Estados' },
    { value: 'good-stock', label: 'Stock Bueno' },
    { value: 'medium-stock', label: 'Stock Medio' },
    { value: 'low-stock', label: 'Stock Bajo' },
    { value: 'out-of-stock', label: 'Sin Stock' }
  ];

  const locations = [
    { value: '', label: 'Todas las Ubicaciones' },
    { value: 'A1-01', label: 'Almacén A1-01' },
    { value: 'A1-02', label: 'Almacén A1-02' },
    { value: 'A2-01', label: 'Almacén A2-01' },
    { value: 'B1-01', label: 'Almacén B1-01' },
    { value: 'B2-01', label: 'Almacén B2-01' }
  ];

  const employees = [
    { value: '', label: 'Todos los Empleados' },
    { value: 'emp001', label: 'Carlos Martínez' },
    { value: 'emp002', label: 'Ana García' },
    { value: 'emp003', label: 'Luis Rodríguez' },
    { value: 'emp004', label: 'María López' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: '',
      category: '',
      stockStatus: '',
      location: '',
      responsibleEmployee: ''
    };
    setFilters(clearedFilters);
    onClearFilters();
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  return (
    <div className="bg-surface rounded-lg border border-border mb-6">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Filter" size={20} className="text-text-secondary" />
            <h3 className="text-lg font-heading-semibold text-text-primary">
              Filtros de Inventario
            </h3>
            {hasActiveFilters && (
              <span className="px-2 py-1 bg-primary-100 text-primary text-xs font-body-medium rounded-full">
                Filtros activos
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={handleClearFilters}
              >
                Limpiar
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Contraer' : 'Expandir'}
            </Button>
          </div>
        </div>
      </div>

      {/* Search Bar - Always Visible */}
      <div className="px-6 py-4">
        <div className="relative">
          <Icon 
            name="Search" 
            size={20} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" 
          />
          <Input
            type="search"
            placeholder="Buscar productos por nombre, SKU o descripción..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Advanced Filters */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Categoría
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Stock Status Filter */}
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Estado de Stock
              </label>
              <select
                value={filters.stockStatus}
                onChange={(e) => handleFilterChange('stockStatus', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {stockStatuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Ubicación
              </label>
              <select
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {locations.map(location => (
                  <option key={location.value} value={location.value}>
                    {location.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Responsible Employee Filter */}
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Empleado Responsable
              </label>
              <select
                value={filters.responsibleEmployee}
                onChange={(e) => handleFilterChange('responsibleEmployee', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {employees.map(employee => (
                  <option key={employee.value} value={employee.value}>
                    {employee.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryFilters;